import { datadogRum } from '@datadog/browser-rum';

import { FINCH_BASE_URL } from 'constants/urls';

/** A legal document the server says this session must present. */
export type LegalDocument = {
  id: string;
  documentType: string;
  /** Displayed in the sheet as the version the acceptance is recorded against. */
  versionLabel: string;
  /** Archival pointer, any shape. Connect never reads it. */
  storageUri?: unknown;
  sourceUrl: string | null;
};

/**
 * Only the fields Connect actually consumes are required. `storageUri` is
 * tolerated in any shape: requiring a string would mean the server changing a
 * field we never read reverts every Preamble to its built-in links, and the
 * failure is invisible because a rejected manifest looks exactly like an
 * absent one.
 */
const isLegalDocument = (value: unknown): value is LegalDocument => {
  if (!value || typeof value !== 'object') return false;

  const doc = value as Record<string, unknown>;
  return (
    typeof doc.id === 'string' &&
    typeof doc.documentType === 'string' &&
    typeof doc.versionLabel === 'string' &&
    (doc.sourceUrl === null || typeof doc.sourceUrl === 'string')
  );
};

/** A consent link has to open the document, so anything unusable is not a URL. */
const isUsableUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};

/** Where to send someone when the catalog entry has no `source_url`. */
const FALLBACK_URLS: Record<string, string> = {
  privacy_policy: `${FINCH_BASE_URL}/legal/privacy`,
  terms_of_service: `${FINCH_BASE_URL}/legal/terms-of-service-for-end-users`,
};

/** Never falls back to the site root: a consent link must open the document. */
export const urlForDocument = (document: LegalDocument): string | undefined => {
  const candidate = document.sourceUrl ?? FALLBACK_URLS[document.documentType];

  return candidate && isUsableUrl(candidate) ? candidate : undefined;
};

const reportFallback = (reason: string, context: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.warn(`${reason}; falling back to built-in links`);
  datadogRum.addError(new Error(`legal documents: ${reason}`), {
    ...context,
    fallback: 'built-in-links',
  });
};

/**
 * Reads the document manifest off the `/auth/validate` response.
 *
 * `undefined` means we could not get a manifest, so the caller keeps its
 * built-in links. `[]` means this connection is already up to date and no
 * consent line is needed. They are not interchangeable.
 */
export const readLegalDocuments = (
  client: unknown,
): LegalDocument[] | undefined => {
  if (!client || typeof client !== 'object') return undefined;

  const raw = (client as Record<string, unknown>).legalDocuments;
  if (raw === undefined) return undefined;

  // Both fallbacks are reported, not just logged: the server still resolves the
  // manifest on its own when the session completes, so a fallback here means
  // what was presented and what gets recorded have diverged, and nothing else
  // would tell us.
  if (!Array.isArray(raw) || !raw.every(isLegalDocument)) {
    reportFallback('legalDocuments was malformed', { raw });
    return undefined;
  }

  // A consent line naming a document nobody can open is worse than none. All
  // or nothing, deliberately: presenting the subset we can link would hide a
  // document the server is about to record an acceptance for, where falling
  // back shows built-in links for every type we know.
  const unlinkable = raw.find((document) => !urlForDocument(document));
  if (unlinkable) {
    reportFallback('no usable URL for legal document type', {
      documentType: unlinkable.documentType,
      sourceUrl: unlinkable.sourceUrl,
    });
    return undefined;
  }

  return raw;
};

/** Link text per type; an unrecognised type still renders, with a generic label. */
const DOCUMENT_TITLES: Record<string, string> = {
  privacy_policy: 'End User Privacy Policy',
  terms_of_service: 'Terms of Service',
};

export const titleForDocument = (document: LegalDocument): string =>
  DOCUMENT_TITLES[document.documentType] ?? 'Legal Terms';
